import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Button, Dialog, DialogActions, Slide } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import history from 'history.js'
import { MOBILE_BREAKPOINT } from 'utils'
import useIsMobile from 'app/hooks/useIsMobile'
import { useToken } from 'app/ducks/auth/hooks'
import { useIsModalOpen } from 'app/ducks/modal/hooks'
import { useInternalCampaigns } from 'app/ducks/internalCampaigns/hooks'
import {
  dismissInternalCampaign,
  followInternalCampaignCta,
} from 'app/ducks/internalCampaigns/actions'
import {
  INTERNAL_CAMPAIGNS_PATH,
  MAX_POPUPS_PER_SESSION,
} from 'app/ducks/internalCampaigns/constants'
import InternalCampaignContent from './InternalCampaignContent'

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />
})

// Same bottom-sheet treatment on mobile as SimpleModal.
const useStyles = makeStyles(() => ({
  container: {
    [`@media only screen and (max-width: ${MOBILE_BREAKPOINT})`]: {
      alignItems: 'end',
    },
  },
  paper: {
    overflowX: 'hidden',
    [`@media only screen and (max-width: ${MOBILE_BREAKPOINT})`]: {
      margin: '0 1rem',
      width: 'calc(100% - 2rem)',
      borderRadius: '.5rem .5rem 0 0',
    },
  },
}))

/**
 * Shows unseen campaigns flagged `show_as_popup`, one at a time, up to
 * MAX_POPUPS_PER_SESSION per page load. Closing ("Fechar") or following
 * the CTA marks the campaign as seen for good. Waits while another global
 * modal (survey prompt, password setup, ...) is open instead of stacking
 * on top of it, and stays out of the way on the Novidades page itself.
 */
const InternalCampaignPopup = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const isMobile = useIsMobile()
  const isOtherModalOpen = useIsModalOpen()
  const { popupCampaigns } = useInternalCampaigns()
  const [current, setCurrent] = useState(null)
  const [shownIds, setShownIds] = useState([])

  useEffect(() => {
    if (current || isOtherModalOpen) return
    if (shownIds.length >= MAX_POPUPS_PER_SESSION) return
    if (history.location.pathname === INTERNAL_CAMPAIGNS_PATH) return

    const next = popupCampaigns.find(({ id }) => !shownIds.includes(id))
    if (!next) return

    setCurrent(next)
    setShownIds((ids) => [...ids, next.id])
  }, [current, isOtherModalOpen, popupCampaigns, shownIds])

  if (!current) return null

  const close = () => {
    dispatch(dismissInternalCampaign(current.id))
    setCurrent(null)
  }

  const followCta = () => {
    dispatch(followInternalCampaignCta(current))
    setCurrent(null)
  }

  return (
    <Dialog
      open
      maxWidth="sm"
      fullWidth
      TransitionComponent={Transition}
      // No onClose: clicking outside or pressing Esc does nothing, so
      // dismissing is always an explicit "Fechar" or CTA click.
      classes={classes}
      aria-labelledby="internal-campaign-popup-title"
      data-testid="internal-campaign-popup"
    >
      <InternalCampaignContent
        campaign={current}
        isNew
        titleId="internal-campaign-popup-title"
      />
      <DialogActions className="px-5 pt-3 pb-4">
        <Button
          fullWidth={isMobile}
          color="secondary"
          variant="outlined"
          onClick={close}
        >
          Fechar
        </Button>
        <Button
          fullWidth={isMobile}
          color="primary"
          variant="contained"
          onClick={followCta}
        >
          {current.cta_label}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const LoginGuardedInternalCampaignPopup = () => {
  const token = useToken()

  if (!token) return null
  return <InternalCampaignPopup />
}

export default LoginGuardedInternalCampaignPopup
